import { useState, useEffect, useRef, type ReactNode } from 'react'
import { Check, X, Pencil } from 'lucide-react'
import { DropdownSelect } from './ui/DropdownSelect'

export interface InlineFieldProps {
  label: string
  value: string
  displayValue?: string | null
  // Rendered instead of the plain-text value when not editing (e.g. a badge).
  displayNode?: ReactNode
  onSave: (val: string) => void
  type?: 'text' | 'email' | 'tel' | 'number' | 'date' | 'datetime-local' | 'url'
  readOnly?: boolean
  placeholder?: string
}

export function InlineField({ label, value, displayValue, displayNode, onSave, type = 'text', readOnly, placeholder }: InlineFieldProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { if (!editing) setDraft(value) }, [value, editing])

  useEffect(() => {
    if (!editing) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDraft(value); setEditing(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [editing, value])

  const save = () => {
    if (draft !== value) onSave(draft)
    setEditing(false)
  }
  const cancel = () => { setDraft(value); setEditing(false) }
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); save() }
    if (e.key === 'Escape') cancel()
  }

  return (
    <div ref={containerRef} className="flex items-start group/field py-1">
      <dt className="w-28 text-xs text-gray-500 font-medium flex-shrink-0 pt-1">{label}</dt>
      <dd className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-1">
            <input
              ref={inputRef}
              autoFocus
              type={type}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              className="input py-0.5 text-sm flex-1"
            />
            <button onMouseDown={e => { e.preventDefault(); save() }} title="Save (Enter)"
              className="p-1 text-green-600 hover:text-green-700 flex-shrink-0">
              <Check className="w-3.5 h-3.5" />
            </button>
            <button onMouseDown={e => { e.preventDefault(); cancel() }} title="Cancel (Esc)"
              className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0">
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1 cursor-pointer" onClick={() => !readOnly && setEditing(true)}>
            {displayNode ?? <span className="text-sm text-gray-900">{(displayValue ?? value) || '—'}</span>}
            {!readOnly && (
              <button onClick={e => { e.stopPropagation(); setEditing(true) }}
                className="opacity-0 group-hover/field:opacity-100 p-0.5 text-gray-400 hover:text-gray-600 transition-opacity">
                <Pencil className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </dd>
    </div>
  )
}

export interface InlineSelectFieldProps {
  label: string
  value: string
  displayValue?: string | null
  options: { value: string; label: string }[]
  onSave: (val: string) => void
  placeholder?: string
  readOnly?: boolean
}

export function InlineSelectField({ label, value, displayValue, options, onSave, placeholder, readOnly }: InlineSelectFieldProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => { if (!editing) setDraft(value) }, [value, editing])

  useEffect(() => {
    if (!editing) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDraft(value); setEditing(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [editing, value])

  const save = () => { if (draft !== value) onSave(draft); setEditing(false) }
  const cancel = () => { setDraft(value); setEditing(false) }

  return (
    <div ref={containerRef} className="flex items-start group/field py-1">
      <dt className="w-28 text-xs text-gray-500 font-medium flex-shrink-0 pt-1">{label}</dt>
      <dd className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-1">
            <DropdownSelect
              value={draft}
              onChange={setDraft}
              options={options}
              placeholder={placeholder ?? 'Select'}
              onClear={placeholder ? () => setDraft('') : undefined}
              clearLabel={placeholder ?? 'Clear'}
              searchable={options.length > 8}
              className="flex-1"
            />
            <button onMouseDown={e => { e.preventDefault(); save() }} title="Save (Enter)"
              className="p-1 text-green-600 hover:text-green-700 flex-shrink-0">
              <Check className="w-3.5 h-3.5" />
            </button>
            <button onMouseDown={e => { e.preventDefault(); cancel() }} title="Cancel (Esc)"
              className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0">
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1 cursor-pointer" onClick={() => !readOnly && setEditing(true)}>
            <span className="text-sm text-gray-900">{(displayValue ?? value) || '—'}</span>
            {!readOnly && (
              <button onClick={e => { e.stopPropagation(); setEditing(true) }}
                className="opacity-0 group-hover/field:opacity-100 p-0.5 text-gray-400 hover:text-gray-600 transition-opacity">
                <Pencil className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </dd>
    </div>
  )
}
